import {
  activateDocumentAssemblerTemplateVersion,
  archiveDocumentAssemblerTemplateVersion,
  publishDocumentAssemblerTemplateVersion,
  readDocumentAssemblerVersionRefusal,
  TDocumentAssemblerVersionRefusal,
} from '@commenda/api';
import { useCallback, useState } from 'react';
import { useNotification } from '../../hooks/useNotification';

interface UseDocumentAssemblerVersionActionsParams {
  templateId: string;
  versionId: string;
  /** Every outcome revalidates: activation moves template.activeVersionId, and a lost race changes findings. */
  onRefresh: () => Promise<void>;
}

export const useDocumentAssemblerVersionActions = ({
  templateId,
  versionId,
  onRefresh,
}: UseDocumentAssemblerVersionActionsParams) => {
  const { setNotification } = useNotification();
  const [isActionPending, setIsActionPending] = useState(false);
  const [refusal, setRefusal] =
    useState<TDocumentAssemblerVersionRefusal | null>(null);

  const runAction = useCallback(
    async (
      action: (templateId: string, versionId: string) => Promise<Response>,
      successTitle: string,
      fallbackMessage: string
    ): Promise<boolean> => {
      setIsActionPending(true);
      setRefusal(null);
      // A refusal the server explained is the better message; the catch below must not relabel it.
      let hasSurfacedRefusal = false;

      try {
        const response = await action(templateId, versionId);

        if (!response.ok) {
          const nextRefusal = await readDocumentAssemblerVersionRefusal(
            response,
            fallbackMessage
          );
          setRefusal(nextRefusal);
          hasSurfacedRefusal = true;
          // The refusal's own errors are the authoritative re-read; refresh so the page agrees with it.
          await onRefresh();
          // Rejecting rather than returning lets a ConfirmationModal stay open on the refusal.
          throw new Error(nextRefusal.message);
        }

        setNotification({ show: true, valid: true, title: successTitle });
        await onRefresh();
        return true;
      } catch (error) {
        // Only an unexplained rejection (network, or a failed revalidation) needs a message here.
        if (!hasSurfacedRefusal) {
          setRefusal({ kind: 'MESSAGE', message: fallbackMessage });
        }
        // ConfirmationModal closes when onConfirm resolves, so a swallowed failure would read as
        // success. Keep rejecting to hold the modal open for retry.
        throw error;
      } finally {
        setIsActionPending(false);
      }
    },
    [templateId, versionId, onRefresh, setNotification]
  );

  const onVersionPublish = useCallback(
    () =>
      runAction(
        publishDocumentAssemblerTemplateVersion,
        'Version published. Mark it active when you are ready.',
        'This version could not be published.'
      ),
    [runAction]
  );

  const onVersionActivate = useCallback(
    () =>
      runAction(
        activateDocumentAssemblerTemplateVersion,
        'Version marked active',
        'This version could not be marked active.'
      ),
    [runAction]
  );

  const onVersionArchive = useCallback(
    () =>
      runAction(
        archiveDocumentAssemblerTemplateVersion,
        'Version archived',
        'This version could not be archived.'
      ),
    [runAction]
  );

  return {
    isActionPending,
    refusal,
    onRefusalDismiss: () => setRefusal(null),
    onVersionPublish,
    onVersionActivate,
    onVersionArchive,
  };
};
